import React, { FC, useEffect, useState, useContext, useMemo } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import axios from 'axios';
import clsx from 'clsx';
import moment from 'moment';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { Button, Link, Types, RichText, Text } from '@marriott/mi-ui-library';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { useSession } from '@marriott/mi-headless-utils';
import {
  FAILURE_CODE,
  handleRedirect,
  PageContext,
  encryptPassword,
  clearSessionAndCookie,
  updatePublicKey,
  updateUserLoginDataLayer,
  signInACDLkeyGenerator,
  enrollACDLkeyGenerator,
} from '../../modules';
import {
  CONSENT_TYPE_ONE_COUNTRY_SET,
  UNIQUE_CONSENT_TYPE_COUNTRY_SET,
  constants,
  ENROLL_ENDPOINT,
  PROFILE_REDIRECTION_URL,
  PROMOTIONS_CONSTANT,
  RFP_URL,
  ENROLLMENT_CONSTANTS,
  ONE_CLICK_JOIN_ERROR,
  BANNER_MSGS_KEYS,
  SWEEPSTAKE_ENROLLMENT_CLICK_TRACK,
  API_ERROR_KEY,
  ENROLL_ENDPOINT_DTT,
  TRACKING_CONSTANTS,
  MARRIOTT_URL,
  MARRIOTT_URL_CHINA,
  ENROLL_PATTERN_EXPERIENCES,
  UNIQUE_CONSENT_GREATER_CHINA,
  nameErrorMessageCode,
  ENROLLMENT_VARIATION_PHONE_REQUIRED,
  ENROLLMENT_VARIATION_PHONE_AND_MFA_REQUIRED,
  ENROLLMENT_VARIATION_PHONE_OPTIONAL,
  FLYOUT_CONFIG_VALUES,
  COUNTRY_SET_CN_VN_KR_JP,
} from '../../modules/utils/constants/constants';
import { EVENT_NAME, STATUS_IN_BOOLEAN } from '../../modules/utils/constants';
import {
  addSubDirectoryPrefix,
  addSubDirectoryPrefixForNonEaa,
  checkChinaLocale,
  getCurrentUrlParams,
  getEnrollmentSourceCodeFromCountry,
  getEnrollmentPreferredLanguageCode,
  skipDecode,
  addClassToBlankTargetLinks,
  checkGreaterChinaLocale,
  isValidValue,
  getCustomClickTrackingPageName,
} from '../../modules/utils/helper';
import { DATALAYER_PAGE_EVENT, updateDataLayerProperties } from '../../modules/utils/accountDataLayer';
import { DefaultConsentForm } from '../../molecules/DefaultConsentForm';
import { useAccountPersistentStore } from '../../modules/store/accountPersistentStore';
import { useCreateAccountStore } from '../../modules/store/createAccountStore';
import { useBannerMessagesStore } from '../../modules/store/bannerMessagesStore';
import { CreateAccountConsentTypes, createAccountReqBody, CustomError } from './CreateAccountConsentsForm.types';
import { StyledCreateAccountConsentsForm } from './CreateAccountConsentsForm.styles';
import { FEATURE_FLAGS } from '@marriott/mi-feature-flag-utils';

const VistanaVacationPolicy = dynamic(() =>
  import('../../molecules/VistanaVacationPolicy').then(mod => mod.VistanaVacationPolicy)
);

// Use named rather than default exports.
export const CreateAccountConsentsForm: FC<CreateAccountConsentTypes> = pageProps => {
  const { model, isDTT } = pageProps;
  const router = useRouter();
  const { query, pathname } = router;
  const promotionId = query ? (query['promotion'] as string) : '';
  const clientId = query ? query['clientId'] : '';
  const clientEnvVars = useClientEnvVarsStore.getState().envVarsObject;
  const { isACDLenabled, updateDatalayer } = useSession();
  const {
    formData,
    joinNowEnabled,
    consents,
    formValidation,
    setErrorKey,
    errorKey,
    remembermeCheckBox,
    transactionDetails,
    tripDetails,
    isDTTforRandQueryParam,
    validSweepEnrollObject,
    goBackLabel,
    goBackUrl,
    setGoBackUrl,
    isValidPartnerPortal,
    isEnrollEAA,
    prefillObj,
    setResetTimer,
    showSendCode,
    selectedOption,
    experienceEnrollmentVariationFromDom,
  } = useCreateAccountStore();
  const { sweepEnrollmentSourceCode, sweepRedirectUrl } = validSweepEnrollObject;
  const { joinNowBtnClicked, setJoinNowBtnClicked } = useCreateAccountStore();
  const [publicKey, setPublicKey] = useState('');
  const { isOCJFlow, setBannerMsgs } = useBannerMessagesStore();
  const [url, setUrl] = useState<string | null>(null);
  const [submitInProgress, setSubmitInProgress] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState(false);
  const [joinNowBtnDisabled, setJoinNowBtnDisabled] = useState(false);
  const [hasErrorKey, setHasErrorKey] = useState(true);
  const countryCode = formData?.country?.id?.toUpperCase();
  const pageContext = useContext(PageContext);
  const localeCountryCode = pageContext?.currentLocale;
  const isEAA = pageContext?.isEAASignIn;
  const isChinaLocale = checkChinaLocale(pageContext?.currentLocale || '');
  const isGreaterChinaLocale = checkGreaterChinaLocale(pageContext?.currentLocale || '');
  const isJoinPromotion = PROMOTIONS_CONSTANT.JOIN_PROMOTION_URL.includes(pathname);
  const featureFlags = pageContext?.featureFlags;
  const isTargetBasedDataLayerEnabled = featureFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_ACCOUNT_ENROLLMENT_TARGET_ENABLED];

  const experienceEnrollmentVariation = isTargetBasedDataLayerEnabled
    ? experienceEnrollmentVariationFromDom
    : featureFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_ACCOUNT_ENROLLMENT_VARIATIONS];
  const isPhoneNumberRequired = experienceEnrollmentVariation === ENROLLMENT_VARIATION_PHONE_REQUIRED;
  const isPhoneNumberAndMFARequired = experienceEnrollmentVariation === ENROLLMENT_VARIATION_PHONE_AND_MFA_REQUIRED;
  const isPhoneNumberOptional = experienceEnrollmentVariation === ENROLLMENT_VARIATION_PHONE_OPTIONAL;
  const hasPhoneNumberConsent = isPhoneNumberRequired || isPhoneNumberAndMFARequired || isPhoneNumberOptional;
  const { ACCEPT_LANGUAGE: currentLocale, TRACKING_PAGE_NAME: pageName } = clientEnvVars;
  const isPatternThree = prefillObj?.partnerExperience === ENROLL_PATTERN_EXPERIENCES.PATTERN_THREE;
  const { PROMOTION_ENROLLMENT, SWEEPSTAKES, INTERNAL, JOIN_NOW_SUBMIT } = TRACKING_CONSTANTS;
  const trackingPageName = promotionId ? PROMOTION_ENROLLMENT : clientId ? SWEEPSTAKES : pageName || '';
  const customClickTrackingPageName = getCustomClickTrackingPageName('', pathname);
  const { setMemberDetailsUxl, setPromoRegistered, setPromoInvalid, setIsEAAEnrollFailed, isDTTFlag } =
    useAccountPersistentStore(state => state);

  const handleClick = () => {
    setJoinNowBtnClicked(true);
  };
  // API call will be made only after the "joinNow" button is clicked and form validation is successful.
  useEffect(() => {
    if (!joinNowBtnClicked) {
      handleSubmit();
    }
  }, [joinNowBtnClicked]);

  const handleSubmit = async () => {
    if (!formValidation) return;
    const urlParams = new URLSearchParams(getCurrentUrlParams());

    // check if enrollmentSourceCode is present in queryParam
    const enrollmentSourceCodeParam = urlParams.get('enrollmentSourceCode');

    // first preference is given to eaa Prefill enrollment source code
    // then check if enrollemnentSourceCode is present in param or promotion id is present or else get enrollment source code from enrollement spurce code list else assgin default value i.e. MRMB
    let enrollmentSourceCodeLocale = prefillObj?.enrollmentSourceCode
      ? prefillObj?.enrollmentSourceCode
      : enrollmentSourceCodeParam ||
        sweepEnrollmentSourceCode ||
        getEnrollmentSourceCodeFromCountry(currentLocale, model?.profileEnrollmentSourceCodeList) ||
        ENROLLMENT_CONSTANTS?.MRMB;

    // check if preferredLanguage is present in enrollment preferred language code list else assgin default value i.e. blank space
    const preferredLanguageCode = getEnrollmentPreferredLanguageCode(
      localeCountryCode,
      model?.enrollmentPreferredLanguageCodesList
    );

    if (isJoinPromotion && promotionId && !enrollmentSourceCodeParam) {
      enrollmentSourceCodeLocale = promotionId;
    }
    const redirectUrl = sweepRedirectUrl
      ? sweepRedirectUrl
      : isJoinPromotion
      ? PROMOTIONS_CONSTANT?.JOIN_CONFIRMATION_PROMOTION_URL.replace('{0}', promotionId)
      : urlParams.get('isRfpFlow')
      ? RFP_URL
      : urlParams.get('returnTo') || (urlParams.get('returnToUrl') ?? PROFILE_REDIRECTION_URL);
    const reqBody: createAccountReqBody = {
      userDetails: {
        firstName: isChinaLocale ? formData?.pinyinEnglishName : formData?.firstName,
        lastName: isChinaLocale ? formData?.pinyinEnglishSurname : formData?.lastName,
        countryCode: formData?.country?.id,
        postalCode: formData?.zipcode?.trim(),
        emailAddress: formData?.email,
        transactionId: transactionDetails,
        phoneNumber: formData?.mobilePhone,
        phoneNumberCountryCode: formData?.mobilePhone ? formData?.mobilePhoneCountryId?.id : '',
        phoneNumberType: formData?.mobilePhone ? constants.MOBILE_PHOHE_CODE : '',
        otp: formData?.verificationCode,
        pwrd: encryptPassword(formData.pwrd, publicKey),
        confirmPwrd: encryptPassword(formData.confirmPwrd, publicKey),
        rememberMe: isEAA ? false : remembermeCheckBox,
        localizeFirstName: isChinaLocale ? formData?.firstName : '',
        localizeLastName: isChinaLocale ? formData?.lastName : '',
        ...(preferredLanguageCode && { preferredLanguage: preferredLanguageCode }),
      },
      userConsents: consents
        .filter(item => item.ctype === 'userConsents' && item.optin === true)
        .map(item => ({ code: item.type, optIn: item.optin })),
      communicationPreferences: consents
        .filter(item => {
          if (countryCode === constants.USA_COUNTRY_CODE) {
            return item.ctype === 'communicationPreferences' && item.optin === true;
          } else {
            return item.ctype === 'communicationPreferences';
          }
        })
        .map(item => ({ type: item.type, optIn: item.optin })),
      ...(isJoinPromotion && {
        promotionIds: [promotionId ? promotionId : ''],
      }) /**join now id is getting undefined 93179 */,
      returnUrl: isEnrollEAA ? prefillObj?.partnerUrl || '' : redirectUrl,
      tripDetails: tripDetails,
      chinaFlow: pageContext?.isMobileAuthFeatureEnabled || isChinaLocale,
      ...(enrollmentSourceCodeLocale && {
        enrollmentSource: enrollmentSourceCodeLocale,
      }),
      pageModuleName: trackingPageName,
      ...(isEnrollEAA && {
        clientId: urlParams.get('client_id') || '',
        queryParams: prefillObj?.queryParams,
      }),
      partnerExperience: prefillObj?.partnerExperience,
      ...(isPatternThree && {
        mfaOption: selectedOption === constants.EMAIL ? constants.OTP_EMAIL : constants.OTP_PHONE,
      }),
      // Add enrollmentVariance if not empty or "''"
      ...(isValidValue(experienceEnrollmentVariation) && {
        enrollmentVariance: experienceEnrollmentVariation,
      }),
    };
    setSubmitInProgress(true);
    const enrollUrl =
      (isOCJFlow ? isDTTFlag : isDTTforRandQueryParam || isDTT) || isEnrollEAA ? ENROLL_ENDPOINT_DTT : ENROLL_ENDPOINT;
    try {
      const response = await axios.post(addSubDirectoryPrefixForNonEaa(enrollUrl, isEnrollEAA), reqBody, {
        headers: {
          'Content-Type': 'application/json',
          isEAA: isEAA ?? '',
        },
        ...(!!sweepEnrollmentSourceCode && {
          params: {
            clientId: clientId,
          },
        }),
      });
      if (response) {
        if (response?.data?.customerDetails) {
          clearSessionAndCookie();
          setMemberDetailsUxl?.(response?.data?.customerDetails);
        }
        // if ACDL is enabled, we are adding SIGNIN event.
        if (isACDLenabled) {
          const enrollEventDetailsHeader = response?.headers?.['enrollmenteventdetails'] || '';
          const enrollACDL = enrollACDLkeyGenerator(enrollEventDetailsHeader);
          const updateDatalayerPayload = {
            loginEventDetails: '',
            isSuccess: true,
            additionalDataLayerProperties: {
              ...signInACDLkeyGenerator(STATUS_IN_BOOLEAN.TRUE, enrollmentSourceCodeLocale),
              ...enrollACDL,
            },
            datalayerAction: {
              eventName: EVENT_NAME.ACCOUNTCREATE,
              update: updateDatalayer,
            },
          };
          updateUserLoginDataLayer(
            updateDatalayerPayload.loginEventDetails,
            updateDatalayerPayload.isSuccess,
            updateDatalayerPayload.additionalDataLayerProperties,
            updateDatalayerPayload.datalayerAction
          );
        }
        if (response?.data?.status?.toUpperCase() === constants.STATUS_SUCCESS) {
          sessionStorage?.setItem(constants.MR_ENROLLED, 'true');
          const { nextStateURI } = response.data;
          if (isJoinPromotion && nextStateURI) {
            if (nextStateURI.includes(PROMOTIONS_CONSTANT.PROMOTION_UNAVAILABLE_URL)) {
              setPromoInvalid?.(true);
            } else {
              setBannerMsgs(BANNER_MSGS_KEYS.NEW_MEMBER_NUMBER_MSG);
              setPromoRegistered?.(promotionId);
            }
          }
          const returnUrl = nextStateURI || (!isEAA ? addSubDirectoryPrefix(PROFILE_REDIRECTION_URL) : '');
          handleRedirect(returnUrl, false, !skipDecode(returnUrl, FLYOUT_CONFIG_VALUES.SKIP_DECODE_URL_FOR_PATH));
        } else if (
          response.request?.responseURL &&
          !response.request?.responseURL.includes(
            (isOCJFlow ? isDTTFlag : isDTT) || isEnrollEAA ? ENROLL_ENDPOINT_DTT : ENROLL_ENDPOINT
          )
        ) {
          window.location.href = response.request?.responseURL;
        }
        setSubmitInProgress(false);
      }
    } catch (error) {
      if (error) {
        const errorBody = (error as CustomError)?.response?.data?.phoenixErrorMessages?.errorMessages;
        let errorMessagesArray: string[] = [];
        if (
          (error as CustomError)?.response?.data?.isRedirect &&
          !(errorBody?.[0] === ONE_CLICK_JOIN_ERROR?.EMAIL_DUPLICATE_ERROR)
        ) {
          // for EAA enroll
          // used to show auto sign failed error banner in login page,
          // here user may get redirected to error page, there we are just clearing the state
          isEnrollEAA && setIsEAAEnrollFailed?.(true);

          handleRedirect((error as CustomError)?.response?.data?.nextStateURI, false);
        } else {
          if (errorBody?.length) {
            errorMessagesArray = [...errorBody];
          } else {
            errorMessagesArray = [API_ERROR_KEY];
          }
          // if ACDL is enabled, we are adding SIGNIN event. if not, existing flow will be followed
          if (isACDLenabled) {
            const enrollEventDetailsHeader =
              (error as CustomError)?.response?.headers?.['enrollmenteventdetails'] || '';
            const enrollACDL = enrollACDLkeyGenerator(enrollEventDetailsHeader);
            const updateDatalayerPayload = {
              loginEventDetails: '',
              isSuccess: false,
              additionalDataLayerProperties: {
                ...signInACDLkeyGenerator(STATUS_IN_BOOLEAN.FALSE, enrollmentSourceCodeLocale),
                ...enrollACDL,
              },
              datalayerAction: {
                eventName: EVENT_NAME.ACCOUNTCREATE,
                update: updateDatalayer,
              },
            };
            updateUserLoginDataLayer(
              updateDatalayerPayload.loginEventDetails,
              updateDatalayerPayload.isSuccess,
              updateDatalayerPayload.additionalDataLayerProperties,
              updateDatalayerPayload.datalayerAction
            );
          } else {
            updateDatalayerOnFailure(
              (error as CustomError)?.response?.headers?.['logineventdetails'] || '',
              (error as CustomError)?.response?.headers?.['enrollmenteventdetails'] || ''
            );
          }
        }
        setErrorKey(errorMessagesArray);
      }
      setSubmitInProgress(false);
      setResetTimer(true);
    }
  };

  const updateDatalayerOnFailure = (loginEventDetails: string, enrollmentEventDetails: string) => {
    const dataLayerProps: { [key: string]: string } = {};
    if (loginEventDetails) {
      dataLayerProps['loginEventDetails'] = loginEventDetails;
    }
    if (sweepEnrollmentSourceCode) {
      dataLayerProps['sweepStakeEnrollment'] = FAILURE_CODE;
      dataLayerProps['partner_name'] = typeof clientId === 'string' ? clientId : clientId?.[0] || '';
    }
    dataLayerProps['enrollmentEventDetails'] = enrollmentEventDetails;
    if (loginEventDetails || enrollmentEventDetails || !!sweepEnrollmentSourceCode) {
      updateDataLayerProperties(dataLayerProps, DATALAYER_PAGE_EVENT.LOGIN_FAILURE);
    }
  };

  useEffect(() => {
    const clickableModalLinks = document.querySelectorAll('a[target="_parent"]');

    const handleLinkClick = (event: MouseEvent) => {
      event.preventDefault();
      const linkHref = (event.currentTarget as HTMLAnchorElement)?.getAttribute('href');
      if (linkHref) {
        setUrl(linkHref);
        setIsOpen(true);
      }
    };

    clickableModalLinks.forEach(link => {
      link?.addEventListener('click', handleLinkClick as EventListener);
    });
  }, [countryCode]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'; // Prevent background scrolling
    } else {
      document.body.style.overflow = 'auto'; // Restore background scrolling
    }
  }, [isOpen]);

  useEffect(() => {
    const urlParams = new URLSearchParams(getCurrentUrlParams());
    const updatedUrl = decodeURIComponent(urlParams.get('returnToUrl') ?? '');

    setGoBackUrl(updatedUrl ?? '');
    updatePublicKey(setPublicKey);
  }, []);

  useEffect(() => {
    if (
      errorKey?.includes(nameErrorMessageCode.firstNameCharLimitErrorMsg) ||
      errorKey?.includes(nameErrorMessageCode.lastNameCharLimitErrorMsg)
    ) {
      setHasErrorKey(true);
    } else {
      setHasErrorKey(false);
    }
  }, [errorKey]);

  useEffect(() => {
    if (
      !joinNowEnabled ||
      submitInProgress ||
      (isPatternThree && !showSendCode) ||
      (isPhoneNumberAndMFARequired && !showSendCode) ||
      hasErrorKey
    ) {
      setJoinNowBtnDisabled(true);
    } else {
      setJoinNowBtnDisabled(false);
    }
  }, [showSendCode, joinNowEnabled, submitInProgress, hasErrorKey]);

  const defaultConsentText = useMemo(() => {
    const modifiedUrl = model?.defaultConsent || '';
    return isEnrollEAA
      ? addClassToBlankTargetLinks(modifiedUrl, '_parent', isChinaLocale ? MARRIOTT_URL_CHINA : MARRIOTT_URL)
      : addSubDirectoryPrefix(modifiedUrl);
  }, [isEnrollEAA]);

  const renderConsentForm = () => {
    if (!countryCode) return null;
    const selectedModel = model?.countryCodesList?.find(entry =>
      isGreaterChinaLocale && UNIQUE_CONSENT_GREATER_CHINA?.includes(countryCode) // show unique consent only on zh-TW and zh-CN locales
        ? entry?.countryCode === countryCode
        : UNIQUE_CONSENT_TYPE_COUNTRY_SET?.includes(countryCode)
        ? entry?.countryCode === countryCode
        : CONSENT_TYPE_ONE_COUNTRY_SET?.includes(countryCode)
        ? entry?.countryCode === 'defaultone'
        : entry?.countryCode === 'defaulttwo'
    );
    return (
      <>
        <DefaultConsentForm
          model={selectedModel}
          countryCode={countryCode}
          errorMessagePersonal={model?.errorMessagePersonal}
          errorMessagePromotions={model?.errorMessagePromotions}
          isEnrollEAA={isEnrollEAA}
        />
        <div className="px-0 mx-auto">
          {!(
            isGreaterChinaLocale
              ? [...UNIQUE_CONSENT_TYPE_COUNTRY_SET, ...UNIQUE_CONSENT_GREATER_CHINA]
              : UNIQUE_CONSENT_TYPE_COUNTRY_SET
          )
            .filter(countryCode => countryCode !== 'US')
            .includes(countryCode) && (
            <RichText
              text={defaultConsentText}
              componentId="default-consent"
              customClass="default-consent__description t-font-s"
            />
          )}
        </div>
        <div
          className={clsx(
            'px-3 mx-auto d-flex flex-column space-bottom copyright-wrapper',
            COUNTRY_SET_CN_VN_KR_JP?.includes(countryCode) && 'mt-4 has-special-consent'
          )}
        >
          {(isPatternThree || hasPhoneNumberConsent) && (
            <RichText
              text={`${model?.mobilePhoneConsentText}` || ''}
              componentId="new-consent"
              customClass="t-font-s"
            />
          )}
        </div>
      </>
    );
  };

  return (
    <StyledCreateAccountConsentsForm
      className="px-0"
      data-testid="createaccountconsentform"
      data-component-name="o-account-createaccountconsentform"
    >
      <div
        className={clsx(
          'create-account-consents-form-container mb-4',
          countryCode &&
            countryCode !== 'US' &&
            (UNIQUE_CONSENT_TYPE_COUNTRY_SET.includes(countryCode) ||
              (isGreaterChinaLocale && UNIQUE_CONSENT_GREATER_CHINA.includes(countryCode)) ||
              countryCode === constants.JAPAN_COUNTRY_CODE)
            ? 'spacing'
            : ''
        )}
      >
        <div
          className={clsx(
            'create-account-consents-form-wrapper container',
            !isEnrollEAA && 'create-account-consents-form-wrapper__border'
          )}
        >
          {isOpen && <VistanaVacationPolicy handleClose={() => setIsOpen(false)} modalURL={url || ''} />}
          {renderConsentForm()}
          <div className="mx-auto d-flex align-items-center flex-column flex-md-row pb-5 pt-3 px-0 space-bottom submit_btn-wrapper">
            <Button
              className={clsx(
                `m-button-m m-button-primary custom-btn submit_btn px-4 py-2 ${
                  joinNowBtnDisabled && 'disabled'
                } create-account-submit-mobile`
              )}
              type={Types.ButtonTypeVariation.Submit}
              buttonCopy={isEnrollEAA ? model?.joinNowLabel : model?.joinTodayLabel}
              callback={handleClick}
              testId={'joinCta'}
              isDisabled={joinNowBtnDisabled}
              custom_click_track_value={
                sweepEnrollmentSourceCode
                  ? SWEEPSTAKE_ENROLLMENT_CLICK_TRACK
                  : `${customClickTrackingPageName}|${JOIN_NOW_SUBMIT}|${INTERNAL}|`
              }
            ></Button>
            {!isEnrollEAA && (
              <Link
                text={isValidPartnerPortal ? goBackLabel : model?.cancelLabel}
                linkClassName="t-font-s mt-4 mt-md-0 mx-md-5 link-underline"
                linkHref={isValidPartnerPortal ? goBackUrl : model?.cancelCtaPath || ''}
                target="_self"
                linkType="internal"
              />
            )}
          </div>
          {isEnrollEAA && (
            <div className="mx-auto d-flex flex-column space-bottom copyright-wrapper">
              <RichText
                text={addClassToBlankTargetLinks(
                  model?.termsAndConditions || '',
                  '_blank',
                  isChinaLocale ? MARRIOTT_URL_CHINA : MARRIOTT_URL
                )}
                componentId="terms-and-conditions"
                customClass={'t-font-s py-3'}
              />
              <Text
                customClass="t-font-xs"
                copyText={model?.copyrightStatement?.replace('{0}', String(moment().year()))}
                fontSize={Types.size.extraSmall}
                element={Types.tags.paragraph}
              />
            </div>
          )}
        </div>
      </div>
    </StyledCreateAccountConsentsForm>
  );
};

export const CreateAccountConsentsFormConfig = {
  emptyLabel: 'createaccountconsentform',
  isEmpty: false,
  resourceType: `mi-aem-account/components/content/createaccountconsentform`,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const CreateAccountConsentsFormEditable = (props: any) => {
  return props?.cqPath?.includes('datapagecomponent') ? (
    <CreateAccountConsentsForm {...props} />
  ) : (
    <EditableComponent config={CreateAccountConsentsFormConfig} {...props}>
      <CreateAccountConsentsForm {...props} />
    </EditableComponent>
  );
};
